import { useEffect, useMemo, useState } from 'react'

/**
 * A hook that returns the current time formatted for a specific timezone.
 * Updates every second to keep the time current.
 *
 * @param timeZone - The IANA timezone string (e.g., 'Europe/Paris', 'America/New_York')
 * @param locale - The locale to use for formatting (defaults to 'fr-FR')
 * @param options - Additional Intl.DateTimeFormatOptions for formatting
 * @returns The formatted time string, or null if the timezone is invalid or not provided
 *
 * @example
 * ```tsx
 * const localTime = useLocalTime('Europe/Paris')
 * // Returns: "14:30:45"
 *
 * const customTime = useLocalTime('America/New_York', 'en-US', {
 *   hour: '2-digit',
 *   minute: '2-digit'
 * })
 * // Returns: "08:30"
 * ```
 */
export const useLocalTime = (
  timeZone: string | null | undefined,
  locale: string = 'fr-FR',
  options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }
): string | null => {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const intervalId = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(intervalId)
  }, [])

  const localTime = useMemo(() => {
    if (!timeZone) return null
    try {
      return now.toLocaleTimeString(locale, {
        timeZone,
        ...options
      })
    } catch {
      // Invalid timezone string
      return null
    }
  }, [now, timeZone, locale, options])

  return localTime
}
